// Needs you (Iris's screen 3): what is waiting on Spencer right now, as one ranked list. His calls first, then dates
// that passed, then leads gone quiet. The rank lives only here, never on the board (Spencer, 2026-10-19). Each row holds
// the step's own controls and its review link; a tap leaves an undo row in place.

import { Link } from 'react-router-dom';
import { rankNeedsYou } from '@zerogap/engine';
import { useCrumb } from '../../frame/Frame';
import { monthDay } from '../../lib/format';
import { usePipeline, type Lead, type Pipeline } from '../../lib/pipeline';
import { Empty, Loading } from '../../ui/kit';
import { BringBack, Discuss, HandTo, LeadValue, Likelihood, ReviewLine, StepControls, UndoRow, useTaps } from './bits';

const GROUPS: Record<'calls' | 'reply' | 'quiet', { label: string; by: string }> = {
  calls: { label: 'Your calls', by: 'by value × likelihood' },
  reply: { label: 'Dates passed', by: 'by value × likelihood' },
  quiet: { label: 'Gone quiet', by: 'by value' },
};

export function NeedsYouList({ p, phone }: { p: Pipeline; phone?: boolean }) {
  const taps = useTaps();
  if (p.loading) return <Loading what="Loading what needs you" />;
  // a lost lead whose re-approach date came due surfaces like a parked one (rule 4, 2026-10-22)
  const candidates = [...p.open, ...p.parked, ...p.lost.filter((l) => l.late)].map((lead) => ({ lead, needsOwner: lead.needsOwner, late: lead.late, quiet: lead.quiet, valueCents: lead.value.cents, likelihoodPct: lead.likelihood }));
  const rows = rankNeedsYou(candidates);
  if (rows.length === 0) return <Empty>Nothing is waiting on you right now.</Empty>;
  const groups = (['calls', 'reply', 'quiet'] as const).map((g) => ({ key: g, ...GROUPS[g], rows: rows.filter((r) => r.group === g) })).filter((g) => g.rows.length > 0);
  // one line under the name, as Iris drew it: the step, then ready · waiting on Owner, or the stage; then the value and likelihood
  const facts = (l: Lead) => {
    const parts: string[] = [];
    if (l.outcome === 'parked') parts.push(`Parked · re-approach ${l.nextTouch ? monthDay(l.nextTouch) : 'not set'}`);
    else if (l.outcome === 'lost') parts.push(`Lost · re-approach ${l.nextTouch ? monthDay(l.nextTouch) : 'not set'}`);
    else if (l.step) parts.push(l.step.name);
    if (l.readyRow) parts.push('ready · waiting on Owner');
    else if (l.stage && l.outcome === 'open') parts.push(l.stage.name);
    return parts.join(' · ');
  };
  return (
    <div className={`pneeds${phone ? ' phone' : ''}`}>
      {groups.map((g) => (
        <div key={g.key} className="stack" style={{ marginTop: 0 }}>
          <div className="pgrp">
            <span>
              {g.label} · {g.rows.length}
            </span>
            <span>{g.by}</span>
          </div>
          {g.rows.map((r) => {
            const l = r.lead.lead;
            const tap = taps.tap(l.id);
            return (
              <div key={l.id} className={`pny${r.lead.needsOwner ? ' needs' : ''}`}>
                <div className="prow">
                  <Link to={`/opportunities/${l.id}`} className="pnm rowlink">
                    {l.name}
                  </Link>
                  <span className="prk num">{r.rank}</span>
                </div>
                {tap ? (
                  <UndoRow tap={tap} onUndo={() => void taps.undo(l.id)} />
                ) : (
                  <>
                    <div className="small psub">
                      {facts(l) || (l.placed ? '' : 'Not placed on a step yet')}
                      {l.outcome === 'open' && !l.readyRow && (
                        <>
                          {' '}· <LeadValue lead={l} /> · <Likelihood lead={l} />
                        </>
                      )}
                    </div>
                    <ReviewLine p={p} lead={l} plain after={<small> · to review</small>} />
                    <div className="pctl">
                      {l.step && l.outcome === 'open' ? (
                        <StepControls p={p} lead={l} onTap={(t) => taps.remember(l.id, t)} />
                      ) : l.outcome === 'parked' || l.outcome === 'lost' ? (
                        <BringBack lead={l} onTap={(t) => taps.remember(l.id, t)} />
                      ) : (
                        <Link to={l.placed ? `/opportunities/${l.id}` : '/pipeline/place'} className="btn">
                          {l.placed ? 'Open' : 'Place it'}
                        </Link>
                      )}
                      <span className="pquietrow">
                        <HandTo p={p} lead={l} />
                        <Discuss p={p} lead={l} />
                      </span>
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}

export function NeedsYouPage() {
  const p = usePipeline();
  useCrumb([{ label: 'Home', to: '/' }, { label: 'Pipeline', to: '/pipeline' }, { label: 'Needs you' }]);
  const n = p.loading ? 0 : rankNeedsYou([...p.open, ...p.parked, ...p.lost.filter((l) => l.late)].map((lead) => ({ lead, needsOwner: lead.needsOwner, late: lead.late, quiet: lead.quiet, valueCents: lead.value.cents, likelihoodPct: lead.likelihood }))).length;
  return (
    <div className="stack">
      <div className="head">
        <div>
          <small className="lb">{new Date().toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}</small>
          <h1>
            Needs you · <span className="num">{n}</span>
          </h1>
        </div>
      </div>
      <NeedsYouList p={p} />
    </div>
  );
}
